import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../../../services/api.service';
import { CenterDTO } from '../../../../DTO/PriorAppointment/CenterDTO';
import { CommunityDTO } from '../../../../DTO/CommunityDTO';
import { CustomFieldDTO } from '../../../../DTO/CustomFieldDTO';
import {
  ActiveServiceIdsForDateDTO,
  AvailableTimesForDateRequestDTO,
  CheckEnabledDatesDTO,
  TimeAvailabilityDTO,
} from '../../../../DTO/PriorAppointment/PeriodsAndHoursDto';
import { ReservationDTO } from '../../../../DTO/PriorAppointment/ReservationDTO';
import { ResourceSlotDTO } from '../../../../DTO/PriorAppointment/ResourceSlotDTO';
import { ServiceDTO } from '../../../../DTO/PriorAppointment/ServiceDTO';

const ITEM_TYPE_GROUP = 'group' as const;
const ITEM_TYPE_RESOURCE = 'resource' as const;

export interface CardItem {
  id: number;
  name: string;
  path?: string;
  itemType: typeof ITEM_TYPE_GROUP | typeof ITEM_TYPE_RESOURCE;
}

@Component({
  selector: 'app-reservation-step1',
  templateUrl: './reservation-step1.component.html',
  styleUrl: './reservation-step1.component.scss',
})
export class ReservationStep1Component implements OnInit {
  readonly ITEM_TYPE_GROUP = ITEM_TYPE_GROUP;
  readonly ITEM_TYPE_RESOURCE = ITEM_TYPE_RESOURCE;
  /** Placeholders per al skeleton de la graella d'hores (alineat amb l'app). */
  readonly skeletonHourPlaceholders = Array.from({ length: 8 }, (_, i) => i);
  /** Skeleton durant la càrrega perezosa de Slots (pills de durada). */
  readonly skeletonDurationPlaceholders = [0, 1, 2, 3];
  /** Skeleton / reinici visual en triar hora (grup amb diversos recursos). */
  loadingGroupResources = false;
  /** Incrementa en cada canvi d'hora (grup) per forçar el `track` del @for de recursos. */
  groupResourceListEpoch = 0;

  isReloadingInfo = false;
  centerDTOs: CenterDTO[] = [];
  pickItems: CardItem[] = [];
  contextCenterName: string | null = null;
  /** Línea secundaria bajo el nombre del centre (dirección o coordenades). */
  contextCenterLocation: string | null = null;
  loadingCenters = false;
  loadingPick = false;
  activeCenter: CenterDTO | null = null;
  selectedPick: CardItem | null = null;

  activeService: ServiceDTO | null = null;
  groupServiceIds: number[] | null = null;
  cachedServicesInGroup: ServiceDTO[] | null = null;
  groupHasNoResources = false;
  durationToResourceIds = new Map<number, number[]>();
  resourceSlots: ResourceSlotDTO[] = [];
  selectedSlot: ResourceSlotDTO | null = null;
  activeServiceIdsForSelectedDay: number[] | null = null;
  /** Carga perezosa de GET Slots tras elegir día (misma idea que l'app). */
  loadingResourceSlots = false;
  loadingDurationSlots = false;
  /** Lista de servicios del centro (evita repetir GET .../Centers/{id}/Services al elegir grupo). */
  private cachedServicesForCenter: ServiceDTO[] | null = null;
  private cachedServicesCenterId: number | null = null;
  /** Ya se aplicaron Slots para el recurso/grupo actual (no dependen del día). */
  private slotsFetchedForCurrentPick = false;
  /** Evita que un `finally` d'una petició antiga deixi `loadingDurationSlots` malament (canvi de dia ràpid). */
  private durationSlotsRequestGen = 0;
  /** Mientras carga detalle + slots + fechas tras elegir grupo/recurso (columna calendario). */
  loadingCalendarPanel = false;

  selectedDay: Date | null = null;
  minDate: Date;
  maxDate: Date;
  disabledDates: Date[] = [];
  availableTimes: TimeAvailabilityDTO[] = [];
  loadHours = false;
  periodHours = 0;
  activeTime = '';
  /** Filtre de graella d'hores (alineat amb l'app). */
  showOnlyAvailableHours = false;
  showAllHours = false;
  /** Recurs concret del grup quan la franja té més d'un recurs disponible. */
  selectedGroupResourceId: number | null = null;
  /** Cache de slots por recurso (grupo), para pintar precio sin nuevas llamadas. */
  private groupSlotsByServiceId = new Map<number, ResourceSlotDTO[]>();

  accesCode: string | null;
  form: FormGroup;
  submitAttempt = false;

  @Input() nextAttempt = new EventEmitter<void>();
  @Output() formValid = new EventEmitter<boolean>();
  @Input() public community: CommunityDTO;
  @Input() public reservation: ReservationDTO;
  @Input() communityModuleId: number | null = null;
  @Input() preReservationLabels = false;

  constructor(
    private apiService: ApiService,
    private actRoute: ActivatedRoute,
  ) {}

  /**
   * Fecha de calendario en zona local (`yyyy-MM-dd`). `toISO()` desplaza a UTC y puede cambiar el día
   * respecto al p-calendar; el backend compara por día civil y dejaba horarios vacíos.
   */
  private priorAppointmentDateForApi(day: Date): string {
    const d = DateTime.fromJSDate(day).startOf('day');
    return (d.toISODate() ?? d.toFormat('yyyy-MM-dd')) as string;
  }

  /** Franges d'hores visibles (filtre + límit 16, com a l'app). */
  get visibleHourSlots(): TimeAvailabilityDTO[] {
    const times = this.availableTimes ?? [];
    const list =
      this.showOnlyAvailableHours && times.length
        ? times.filter((t) => this.timeSlotAvailableCount(t) > 0)
        : [...times];
    if (this.showAllHours) {
      return list;
    }
    return list.slice(0, 16);
  }

  get hasMoreHours(): boolean {
    const times = this.availableTimes ?? [];
    const list =
      this.showOnlyAvailableHours && times.length
        ? times.filter((t) => this.timeSlotAvailableCount(t) > 0)
        : [...times];
    return list.length > 16 && !this.showAllHours;
  }

  get groupResourcesForTimeSlot(): ServiceDTO[] {
    if (this.selectedPick?.itemType !== ITEM_TYPE_GROUP || !this.cachedServicesInGroup?.length) {
      return [];
    }
    const ids = this.reservation.resourceIdsForSelectedDuration;
    if (!ids?.length) {
      return [];
    }
    return this.cachedServicesInGroup.filter((s) => ids.includes(s.id));
  }

  get showGroupResourcePicker(): boolean {
    return (
      this.selectedPick?.itemType === ITEM_TYPE_GROUP &&
      !!this.activeTime &&
      this.groupResourcesForTimeSlot.length > 1
    );
  }

  get selectedDurationMinutes(): number | null {
    if (this.selectedSlot?.durationMinutes != null) {
      return this.selectedSlot.durationMinutes;
    }
    return Number.isFinite(this.periodHours) && this.periodHours > 0 ? this.periodHours : null;
  }

  needsGroupResourcePicked(): boolean {
    return this.showGroupResourcePicker && this.selectedGroupResourceId == null;
  }

  onShowOnlyAvailableHoursChange(): void {
    this.showAllHours = false;
  }

  selectGroupResource(svc: ServiceDTO): void {
    this.loadingGroupResources = false;
    this.applyGroupResourceSelection(svc.id);
  }

  getGroupResourcePrice(
    serviceId: number,
  ): { durationMinutes: number; userPrice: number; isFree: boolean } | null {
    const selectedDuration = this.selectedDurationMinutes;
    if (!selectedDuration) {
      return null;
    }
    const slots = this.groupSlotsByServiceId.get(serviceId) ?? [];
    const slot = slots.find((s) => s.durationMinutes === selectedDuration);
    if (!slot) {
      return null;
    }
    const service = this.cachedServicesInGroup?.find((s) => s.id === serviceId) ?? null;
    const userPrice = this.computeUserPriceFromSlot(slot, service);
    if (userPrice == null) {
      return null;
    }
    return {
      durationMinutes: selectedDuration,
      userPrice,
      isFree: userPrice === 0,
    };
  }

  private computeUserPriceFromSlot(slot: ResourceSlotDTO, service: ServiceDTO | null): number | null {
    const basePrice = Number(slot.basePrice ?? 0);
    const ivaPercent = Number(slot.ivaPercent ?? 0);
    if (!Number.isFinite(basePrice) || !Number.isFinite(ivaPercent)) {
      return null;
    }
    const totalWithIva = basePrice + (basePrice * ivaPercent) / 100;
    const feeAmount = Number(slot.feeAmount ?? 0);
    const feeChargedTo = Number((service as { feeChargedTo?: number } | null)?.feeChargedTo ?? 1);
    const userPrice = feeChargedTo === 0 ? totalWithIva + feeAmount : totalWithIva;
    return this.roundTo2(Math.max(0, userPrice));
  }

  private roundTo2(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private clearReservationPrice(): void {
    this.reservation.basePrice = undefined;
    this.reservation.ivaAmount = undefined;
    this.reservation.feeAmount = undefined;
    this.reservation.extrasAmount = undefined;
    this.reservation.totalAmount = undefined;
  }

  private setReservationPriceFromSlot(slot: ResourceSlotDTO, service: ServiceDTO | null): void {
    const basePrice = Number(slot.basePrice ?? 0);
    const ivaPercent = Number(slot.ivaPercent ?? 0);
    const feeAmount = Number(slot.feeAmount ?? 0);
    if (!Number.isFinite(basePrice) || !Number.isFinite(ivaPercent) || !Number.isFinite(feeAmount)) {
      this.clearReservationPrice();
      return;
    }
    const ivaAmount = (basePrice * ivaPercent) / 100;
    const subtotalWithTax = basePrice + ivaAmount;
    const feeChargedTo = Number((service as { feeChargedTo?: number } | null)?.feeChargedTo ?? 1);
    const totalAmount = feeChargedTo === 0 ? subtotalWithTax + feeAmount : subtotalWithTax;
    this.reservation.basePrice = this.roundTo2(Math.max(0, basePrice));
    this.reservation.ivaAmount = this.roundTo2(Math.max(0, ivaAmount));
    this.reservation.feeAmount = this.roundTo2(Math.max(0, feeAmount));
    this.reservation.extrasAmount = 0;
    this.reservation.totalAmount = this.roundTo2(Math.max(0, totalAmount));
  }

  private getGroupSlotForServiceAndDuration(serviceId: number, durationMinutes: number): ResourceSlotDTO | null {
    const slots = this.groupSlotsByServiceId.get(serviceId) ?? [];
    return slots.find((s) => s.durationMinutes === durationMinutes) ?? null;
  }

  private syncReservationPriceForCurrentSelection(): void {
    const duration = this.selectedDurationMinutes;
    const service = this.activeService;
    if (!duration || !service?.id) {
      this.clearReservationPrice();
      return;
    }
    const isGroup = this.selectedPick?.itemType === ITEM_TYPE_GROUP;
    const slot = isGroup
      ? this.getGroupSlotForServiceAndDuration(service.id, duration)
      : this.resourceSlots.find((s) => s.durationMinutes === duration) ?? null;
    if (!slot) {
      this.clearReservationPrice();
      return;
    }
    this.setReservationPriceFromSlot(slot, service);
  }

  private applyGroupResourceSelection(serviceId: number): void {
    const svc = this.cachedServicesInGroup?.find((s) => s.id === serviceId) ?? null;
    if (!svc) {
      return;
    }
    this.selectedGroupResourceId = serviceId;
    this.reservation.serviceId = svc.id;
    this.reservation.serviceName = svc.name;
    this.activeService = svc;
    this.form.patchValue({ service: svc });
    this.syncReservationPriceForCurrentSelection();
  }

  /** Places disponibles per a la franja (suporta resposta legacy amb `reserved`). */
  private timeSlotAvailableCount(t: TimeAvailabilityDTO): number {
    if (t.availableSlots != null) {
      return t.availableSlots;
    }
    return t.reserved === true ? 0 : 1;
  }

  get filteredResourceSlots(): ResourceSlotDTO[] {
    const slots = this.resourceSlots;
    if (!slots?.length) {
      return [];
    }
    if (!this.durationToResourceIds.size) {
      return [...slots].sort((a, b) => a.durationMinutes - b.durationMinutes);
    }
    const activeIds = this.activeServiceIdsForSelectedDay;
    if (activeIds == null) {
      return [...slots].sort((a, b) => a.durationMinutes - b.durationMinutes);
    }
    if (activeIds.length === 0) {
      return [];
    }
    const list = slots.filter((slot) => {
      const ids = this.durationToResourceIds.get(slot.durationMinutes);
      return ids?.some((id) => activeIds.includes(id)) ?? false;
    });
    return [...list].sort((a, b) => a.durationMinutes - b.durationMinutes);
  }

  private normalizeTimeAvailabilityList(raw: TimeAvailabilityDTO[] | null | undefined): TimeAvailabilityDTO[] {
    return (raw ?? [])
      .map((x) => {
        const row = x as unknown as Record<string, unknown>;
        const time =
          typeof row['time'] === 'string'
            ? row['time']
            : typeof row['Time'] === 'string'
              ? (row['Time'] as string)
              : '';
        const totalSlots = (row['totalSlots'] ?? row['TotalSlots']) as number | undefined;
        const availableSlots = (row['availableSlots'] ?? row['AvailableSlots']) as number | undefined;
        const reserved = (row['reserved'] ?? row['Reserved']) as boolean | undefined;
        let availableResourceIds = row['availableResourceIds'] as number[] | undefined;
        if (!Array.isArray(availableResourceIds) && Array.isArray(row['AvailableResourceIds'])) {
          availableResourceIds = row['AvailableResourceIds'] as number[];
        }
        return {
          time,
          totalSlots,
          availableSlots,
          reserved,
          availableResourceIds,
        } as TimeAvailabilityDTO;
      })
      .filter((x) => !!x.time);
  }

  ngOnInit(): void {
    if (this.reservation?.centerId) {
      this.isReloadingInfo = true;
    }
    this.actRoute.queryParamMap.subscribe((params) => {
      this.accesCode = params.get('accesCode');
    });
    const today = new Date();
    this.minDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.maxDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
    this.initializeForm();
    this.fetchCenters();
    this.nextAttempt.subscribe(() => this.validateForm());
  }

  private initializeForm(): void {
    this.form = new FormGroup({
      center: new FormControl<number | null>(null, Validators.required),
      service: new FormControl<ServiceDTO | null>(null, Validators.required),
      type: new FormControl<number | null>(null, Validators.required),
      date: new FormControl<Date | null>(null, Validators.required),
      time: new FormControl<string | null>(null, Validators.required),
    });
    this.form.valueChanges.subscribe(() => {
      Object.values(this.form.controls).forEach((c) => c.markAsUntouched());
    });
  }

  private async fetchCenters(): Promise<void> {
    this.loadingCenters = true;
    try {
      const res = await firstValueFrom(
        this.apiService.getCenters(this.community?.code, this.accesCode, this.communityModuleId),
      );
      this.centerDTOs = res ?? [];
      if (this.reservation?.centerId) {
        const c = this.centerDTOs.find((x) => x.id === this.reservation.centerId) ?? null;
        if (c) {
          await this.applyCenterSelection(c, false);
          if (this.reservation.resourceGroupId != null || this.reservation.resourceId != null) {
            await this.restorePickAndScheduleFromReservation();
          }
        }
        this.isReloadingInfo = false;
      }
    } finally {
      this.loadingCenters = false;
    }
  }

  /** Restaura flujo grupo/recurso + fecha/hora si la reserva en edición ya tenía datos. */
  private async restorePickAndScheduleFromReservation(): Promise<void> {
    const gid = this.reservation.resourceGroupId;
    const rid = this.reservation.resourceId;
    let pick: CardItem | null = null;
    if (gid != null) {
      pick = this.pickItems.find((i) => i.itemType === ITEM_TYPE_GROUP && i.id === gid) ?? null;
    } else if (rid != null) {
      pick = this.pickItems.find((i) => i.itemType === ITEM_TYPE_RESOURCE && i.id === rid) ?? null;
    }
    if (!pick) {
      return;
    }
    this.selectedPick = pick;
    if (pick.itemType === ITEM_TYPE_GROUP) {
      await this.loadGroupFlow(pick.id);
    } else {
      await this.loadResourceFlow(pick.id);
    }
    if (this.reservation.startTime) {
      await this.ensureResourceSlotsLoaded();
      this.selectedDay = DateTime.fromISO(this.reservation.startTime).toJSDate();
      await this.updateActiveServiceIdsForSelectedDay();
      if (this.resourceSlots.length === 1) {
        this.selectedSlot = this.resourceSlots[0];
        this.periodHours = this.selectedSlot.durationMinutes;
      }
      await this.showHours();
      const start = this.reservation.startTime ? DateTime.fromISO(this.reservation.startTime) : null;
      this.activeTime = start?.isValid ? start.toFormat('HH:mm') : '';
      const slot = this.activeTime ? this.availableTimes.find((s) => s.time === this.activeTime) : undefined;
      if (slot?.availableResourceIds != null) {
        this.reservation.resourceIdsForSelectedDuration = [...slot.availableResourceIds];
      }
      const ids = this.reservation.resourceIdsForSelectedDuration;
      if (this.selectedPick?.itemType === ITEM_TYPE_GROUP && ids?.length === 1) {
        this.applyGroupResourceSelection(ids[0]);
      } else if (this.selectedPick?.itemType === ITEM_TYPE_GROUP && ids && ids.length > 1 && this.reservation.serviceId) {
        const sid = this.reservation.serviceId;
        if (ids.includes(sid)) {
          this.applyGroupResourceSelection(sid);
        }
      }
      this.form.patchValue({
        date: this.selectedDay,
        time: this.activeTime || null,
        type: 0,
        service: this.activeService,
      });
      this.reservation.type = 0;
    }
  }

  async setCenter(center: CenterDTO): Promise<void> {
    await this.applyCenterSelection(center, true);
  }

  private async applyCenterSelection(center: CenterDTO, resetPick: boolean): Promise<void> {
    const previousCenterId = this.activeCenter?.id ?? null;
    if (previousCenterId !== center.id) {
      this.cachedServicesForCenter = null;
      this.cachedServicesCenterId = null;
    }
    this.activeCenter = center;
    this.form.patchValue({ center: center.id });
    this.reservation.centerId = center.id;
    this.reservation.centerName = center.name;
    this.contextCenterName = (center.name ?? '').trim() || null;
    this.contextCenterLocation = null;
    if (this.community?.code) {
      this.loadingPick = true;
      this.pickItems = [];
    }
    const detail = await firstValueFrom(
      this.apiService.getCenterDetail(this.community.code, center.id, this.communityModuleId, this.accesCode),
    ).catch(() => center);
    this.activeCenter = detail ?? center;
    this.refreshCenterContextLabels();
    if (this.activeCenter?.rgpdTranslatable) {
      this.reservation.rgpd = this.activeCenter.rgpdTranslatable;
    }
    if (resetPick) {
      this.resetScheduleUi();
      this.selectedPick = null;
      this.reservation.resourceGroupId = undefined;
      this.reservation.resourceGroupName = undefined;
      this.reservation.resourceId = undefined;
      this.reservation.resourceName = undefined;
      this.reservation.resourceIdsForSelectedDuration = undefined;
    }
    await this.fetchGroupsAndResources();
  }

  private resetScheduleUi(): void {
    this.activeService = null;
    this.groupServiceIds = null;
    this.cachedServicesInGroup = null;
    this.groupHasNoResources = false;
    this.durationToResourceIds = new Map();
    this.resourceSlots = [];
    this.selectedSlot = null;
    this.activeServiceIdsForSelectedDay = null;
    this.selectedDay = null;
    this.disabledDates = [];
    this.availableTimes = [];
    this.activeTime = '';
    this.loadHours = false;
    this.loadingDurationSlots = false;
    this.durationSlotsRequestGen++;
    this.periodHours = 0;
    this.form.patchValue({ service: null, type: null, date: null, time: null });
    this.reservation.serviceId = undefined as unknown as number;
    this.reservation.serviceName = undefined as unknown as string;
    this.reservation.startTime = undefined as unknown as string;
    this.reservation.endTime = undefined as unknown as string;
    this.reservation.type = undefined as unknown as number;
    this.reservation.resourceIdsForSelectedDuration = undefined;
    this.slotsFetchedForCurrentPick = false;
    this.loadingResourceSlots = false;
    this.showOnlyAvailableHours = false;
    this.showAllHours = false;
    this.selectedGroupResourceId = null;
    this.loadingGroupResources = false;
    this.groupResourceListEpoch = 0;
    this.groupSlotsByServiceId = new Map();
    this.clearReservationPrice();
    this.clearReservationHold();
  }

  private clearReservationHold(): void {
    this.reservation.id = undefined as unknown as number;
    this.reservation.status = undefined;
    this.reservation.reservedToken = undefined;
    this.reservation.reservedUntil = undefined;
  }

  private async fetchGroupsAndResources(): Promise<void> {
    if (!this.activeCenter?.id || !this.community?.code) {
      return;
    }
    this.loadingPick = true;
    this.pickItems = [];
    this.contextCenterName = (this.activeCenter?.name ?? '').trim() || null;
    this.contextCenterLocation = null;
    try {
      const servicesOrError = await firstValueFrom(
        this.apiService.getServicesByCenterId(this.community.code, this.activeCenter.id, this.communityModuleId, this.accesCode),
      ).catch(() => null as ServiceDTO[] | null);
      if (servicesOrError != null) {
        this.cachedServicesForCenter = servicesOrError;
        this.cachedServicesCenterId = this.activeCenter.id;
      }
      const c = this.activeCenter as CenterDTO & { items?: any[] };
      this.contextCenterName = (c?.name ?? this.reservation?.centerName ?? '').trim() || null;
      this.refreshCenterContextLabelsFromDetail(c);
      const items = (c as any)?.items ?? [];
      const groupIdsWithServices = new Set<number>();
      if (servicesOrError != null) {
        for (const s of servicesOrError) {
          const gid = s.resourceGroupId ?? s.ResourceGroupId;
          if (gid != null) {
            groupIdsWithServices.add(gid);
          }
        }
      }
      const groups = (items || [])
        .filter((i: any) => i?.type === 'group')
        .filter((g: any) => servicesOrError === null || groupIdsWithServices.has(g.id))
        .map((g: any) => ({
          id: g.id,
          name: g.name,
          path: g.path,
          itemType: ITEM_TYPE_GROUP,
        }));
      const resources = (items || [])
        .filter(
          (i: any) =>
            i?.type === 'resource' && (i.resourceGroupId ?? i.ResourceGroupId) == null,
        )
        .map((r: any) => ({
          id: r.id,
          name: r.name,
          path: r.path,
          itemType: ITEM_TYPE_RESOURCE,
        }));
      this.pickItems = [...groups, ...resources];
    } finally {
      this.loadingPick = false;
    }
  }

  async onPickItem(item: CardItem): Promise<void> {
    this.loadingCalendarPanel = true;
    try {
      this.resetScheduleUi();
      this.selectedPick = item;
      if (item.itemType === ITEM_TYPE_GROUP) {
        this.reservation.resourceGroupId = item.id;
        this.reservation.resourceGroupName = item.name;
        this.reservation.resourceId = undefined;
        this.reservation.resourceName = undefined;
        await this.loadGroupFlow(item.id);
      } else {
        this.reservation.resourceId = item.id;
        this.reservation.resourceName = item.name;
        this.reservation.resourceGroupId = undefined;
        this.reservation.resourceGroupName = undefined;
        await this.loadResourceFlow(item.id);
      }
    } finally {
      this.loadingCalendarPanel = false;
    }
  }

  private async loadResourceFlow(resourceId: number): Promise<void> {
    const svc = await firstValueFrom(
      this.apiService.getServiceDetail(this.community.code, resourceId, this.communityModuleId, this.accesCode),
    );
    this.activeService = svc;
    this.reservation.serviceId = svc.id;
    this.reservation.serviceName = svc.name;
    this.groupServiceIds = null;
    this.cachedServicesInGroup = null;
    this.groupSlotsByServiceId = new Map();
    this.durationToResourceIds = new Map();
    this.reservation.type = 0;
    this.form.patchValue({ service: svc, type: 0 });
    this.periodHours = svc.slot;
    this.resourceSlots = [];
    this.durationToResourceIds = new Map();
    this.selectedSlot = null;
    this.slotsFetchedForCurrentPick = false;
    this.clearReservationPrice();
    await this.checkDisabledDates([svc.id]);
  }

  private async loadGroupFlow(groupId: number): Promise<void> {
    const centerId = this.activeCenter!.id;
    let services: ServiceDTO[] | null = null;
    if (this.cachedServicesCenterId === centerId && this.cachedServicesForCenter != null) {
      services = this.cachedServicesForCenter;
    } else {
      services = await firstValueFrom(
        this.apiService.getServicesByCenterId(this.community.code, centerId, this.communityModuleId, this.accesCode),
      ).catch(() => null);
      if (services != null) {
        this.cachedServicesForCenter = services;
        this.cachedServicesCenterId = centerId;
      }
    }
    const inGroup = (services ?? []).filter(
      (s) => s.resourceGroupId === groupId || s.ResourceGroupId === groupId,
    );
    if (!inGroup.length) {
      this.groupHasNoResources = true;
      this.activeService = null;
      this.groupServiceIds = null;
      this.cachedServicesInGroup = null;
      this.resourceSlots = [];
      return;
    }
    this.groupHasNoResources = false;
    this.cachedServicesInGroup = inGroup;
    this.groupSlotsByServiceId = new Map();
    const first = inGroup[0];
    this.activeService = first;
    this.reservation.serviceId = first.id;
    this.reservation.serviceName = first.name;
    this.reservation.type = 0;
    this.form.patchValue({ service: first, type: 0 });
    this.groupServiceIds = inGroup.map((s) => s.id);
    this.periodHours = first.slot;
    this.resourceSlots = [];
    this.durationToResourceIds = new Map();
    this.selectedSlot = null;
    this.slotsFetchedForCurrentPick = false;
    this.clearReservationPrice();
    await this.checkDisabledDates(inGroup.map((s) => s.id));
  }

  /** GET Slots (una o N llamadas en grupo) solo cuando hace falta: primer día elegido o restauración con datos. */
  private async ensureResourceSlotsLoaded(): Promise<void> {
    if (this.slotsFetchedForCurrentPick || !this.activeService?.id) {
      return;
    }
    const inGroup = this.cachedServicesInGroup;
    if (inGroup?.length) {
      await this.fetchAndApplyGroupSlots(inGroup);
    } else {
      await this.fetchAndApplyResourceSlots(this.activeService.id);
    }
    this.slotsFetchedForCurrentPick = true;
  }

  private async fetchAndApplyGroupSlots(servicesInGroup: ServiceDTO[]): Promise<void> {
    const slotsPerService = await Promise.all(
      servicesInGroup.map((s) =>
        firstValueFrom(this.apiService.getResourceSlots(this.community.code, s.id, this.communityModuleId, this.accesCode)),
      ),
    );
    const slotsByService = new Map<number, ResourceSlotDTO[]>();
    const durationMap = new Map<number, number[]>();
    const uniqueSlots: ResourceSlotDTO[] = [];
    slotsPerService.forEach((slots, index) => {
      const serviceId = servicesInGroup[index]?.id;
      if (serviceId != null) {
        slotsByService.set(serviceId, slots || []);
      }
      (slots || []).forEach((s) => {
        const d = s.durationMinutes;
        if (!durationMap.has(d)) {
          durationMap.set(d, []);
          uniqueSlots.push({ durationMinutes: d, resourceId: serviceId });
        }
        const ids = durationMap.get(d)!;
        if (serviceId != null && !ids.includes(serviceId)) {
          ids.push(serviceId);
        }
      });
    });
    this.groupSlotsByServiceId = slotsByService;
    this.durationToResourceIds = durationMap;
    const first = servicesInGroup[0];
    const merged =
      uniqueSlots.length > 0
        ? uniqueSlots
        : first?.slot > 0
          ? [{ id: 0, resourceId: first.id, durationMinutes: first.slot }]
          : [];
    this.resourceSlots = merged;
    if (merged.length === 1) {
      this.selectSlot(merged[0]);
    }
  }

  private async fetchAndApplyResourceSlots(serviceId: number): Promise<void> {
    const slots = await firstValueFrom(
      this.apiService.getResourceSlots(this.community.code, serviceId, this.communityModuleId, this.accesCode),
    );
    this.durationToResourceIds = new Map();
    this.groupSlotsByServiceId = new Map();
    let slotList: ResourceSlotDTO[];
    if (slots?.length) {
      const byDuration = new Map<number, ResourceSlotDTO>();
      slots.forEach((s) => {
        if (!byDuration.has(s.durationMinutes)) {
          byDuration.set(s.durationMinutes, { ...s, resourceId: serviceId });
        }
      });
      slotList = Array.from(byDuration.values());
    } else {
      const s = this.activeService!;
      slotList = s.slot > 0 ? [{ id: 0, resourceId: s.id, durationMinutes: s.slot }] : [];
    }
    this.resourceSlots = slotList;
    if (slotList.length === 1) {
      this.selectSlot(slotList[0]);
    }
  }

  private async checkDisabledDates(serviceIds: number[]): Promise<void> {
    if (!this.activeCenter || !serviceIds?.length) {
      return;
    }
    const data = new CheckEnabledDatesDTO();
    data.centerId = this.activeCenter.id;
    data.serviceIds = serviceIds;
    const c = await firstValueFrom(
      this.apiService.disabledDates(this.community.code, data, this.communityModuleId, this.accesCode),
    );
    const max = c?.maxDate ? DateTime.fromISO(c.maxDate) : null;
    if (max?.isValid) {
      this.maxDate = max.toJSDate();
    }
    this.disabledDates = (c?.disabledDates || [])
      .map((x) => DateTime.fromISO(x))
      .filter((d) => d.isValid)
      .map((d) => d.toJSDate());
  }

  selectSlot(slot: ResourceSlotDTO): void {
    this.selectedGroupResourceId = null;
    this.selectedSlot = slot;
    this.periodHours = slot.durationMinutes;
    if (this.durationToResourceIds.size) {
      let ids = this.durationToResourceIds.get(slot.durationMinutes) ?? [];
      const activeIds = this.activeServiceIdsForSelectedDay;
      if (activeIds?.length) {
        ids = ids.filter((id) => activeIds.includes(id));
      }
      this.reservation.resourceIdsForSelectedDuration = ids;
    } else {
      this.reservation.resourceIdsForSelectedDuration = undefined;
    }
    this.syncReservationPriceForCurrentSelection();
    if (this.selectedDay) {
      this.availableTimes = [];
      void this.showHours().catch(() => {
        this.availableTimes = [];
      });
    }
  }

  async onCalendarSelect(selected?: Date): Promise<void> {
    if (selected instanceof Date) {
      this.selectedDay = selected;
    }
    const day = this.selectedDay;
    if (!day) {
      return;
    }
    this.activeTime = '';
    this.showAllHours = false;
    this.showOnlyAvailableHours = false;
    this.selectedGroupResourceId = null;
    this.loadingGroupResources = false;
    // Al cambiar de día hay que volver a elegir duración (como en la app): si se mantiene
    // selectedSlot, autoSelectSingleFilteredSlot no llama a selectSlot/showHours y la UI queda
    // con la pill marcada pero sin franjas horarias.
    this.selectedSlot = null;
    this.periodHours = 0;
    this.reservation.resourceIdsForSelectedDuration = undefined;
    this.form.patchValue({ time: null, date: day });
    this.reservation.startTime = undefined as unknown as string;
    this.reservation.endTime = undefined as unknown as string;
    this.availableTimes = [];
    const needInitialSlots = !this.slotsFetchedForCurrentPick;
    if (needInitialSlots) {
      this.loadingResourceSlots = true;
    }
    try {
      await this.ensureResourceSlotsLoaded();
    } catch {
      this.resourceSlots = [];
      this.durationToResourceIds = new Map();
      this.slotsFetchedForCurrentPick = false;
    } finally {
      if (needInitialSlots) {
        this.loadingResourceSlots = false;
      }
    }

    await this.updateActiveServiceIdsForSelectedDay();
    this.clearSelectedSlotIfInvalid();

    if (this.autoSelectSingleFilteredSlot()) {
      return;
    }

    if (this.selectedSlot) {
      await this.showHours();
    } else {
      this.reservation.resourceIdsForSelectedDuration = undefined;
      this.loadHours = false;
    }
  }

  private async updateActiveServiceIdsForSelectedDay(): Promise<void> {
    const serviceIds = this.groupServiceIds ?? (this.activeService ? [this.activeService.id] : []);
    if (!this.selectedDay || !this.activeCenter || !serviceIds.length) {
      this.activeServiceIdsForSelectedDay = null;
      this.loadingDurationSlots = false;
      this.durationSlotsRequestGen++;
      return;
    }
    const requestGen = ++this.durationSlotsRequestGen;
    this.loadingDurationSlots = true;
    try {
      const body: ActiveServiceIdsForDateDTO = {
        centerId: this.activeCenter.id,
        serviceIds,
        date: this.priorAppointmentDateForApi(this.selectedDay),
      };
      const res = await firstValueFrom(
        this.apiService.getActiveServiceIdsForDate(this.community.code, body, this.communityModuleId, this.accesCode),
      );
      if (requestGen !== this.durationSlotsRequestGen) {
        return;
      }
      this.activeServiceIdsForSelectedDay = res?.activeServiceIds ?? null;
      this.clearSelectedSlotIfInvalid();
      this.autoSelectSingleFilteredSlot();
    } finally {
      if (requestGen === this.durationSlotsRequestGen) {
        this.loadingDurationSlots = false;
      }
    }
  }

  private clearSelectedSlotIfInvalid(): void {
    if (!this.durationToResourceIds.size || !this.selectedSlot) {
      return;
    }
    const activeIds = this.activeServiceIdsForSelectedDay;
    if (activeIds == null) {
      return;
    }
    if (activeIds.length === 0) {
      this.selectedSlot = null;
      return;
    }
    const idsForDuration = this.durationToResourceIds.get(this.selectedSlot.durationMinutes);
    if (!idsForDuration?.some((id) => activeIds.includes(id))) {
      this.selectedSlot = null;
    }
  }

  private autoSelectSingleFilteredSlot(): boolean {
    if (this.filteredResourceSlots.length !== 1) {
      return false;
    }

    this.selectSlot(this.filteredResourceSlots[0]);
    return true;
  }

  async showHours(): Promise<void> {
    const day = this.selectedDay;
    if (!this.activeService || !day || !this.activeCenter) {
      this.loadHours = false;
      return;
    }
    if (this.resourceSlots.length > 0 && !this.selectedSlot) {
      this.loadHours = false;
      return;
    }
    this.loadHours = true;
    this.activeTime = '';
    this.form.patchValue({ time: null });
    this.reservation.startTime = undefined as unknown as string;
    this.reservation.endTime = undefined as unknown as string;
    const serviceIds = this.groupServiceIds ?? [this.activeService.id];
    const body: AvailableTimesForDateRequestDTO = {
      centerId: this.activeCenter.id,
      serviceIds,
      selectedDate: this.priorAppointmentDateForApi(day),
      periodMinutes: this.periodHours,
      serviceIdForSchedule: this.activeService.id,
    };
    try {
      let times = await firstValueFrom(
        this.apiService.getAvailableTimesForDate(this.community.code, body, this.communityModuleId, this.accesCode),
      );
      times = this.normalizeTimeAvailabilityList(times);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(day);
      selected.setHours(0, 0, 0, 0);
      if (selected.getTime() === today.getTime()) {
        const now = new Date();
        times = (times || []).map((slot) => {
          const [h, m] = slot.time.split(':').map(Number);
          const slotDate = new Date();
          slotDate.setHours(h, m, 0, 0);
          if (slotDate <= now) {
            return { ...slot, availableSlots: 0, availableResourceIds: [] };
          }
          return slot;
        });
      }
      this.availableTimes = times ?? [];
    } finally {
      this.loadHours = false;
    }
  }

  selectTime(time: string): void {
    const day = this.selectedDay;
    if (!day) {
      this.activeTime = time;
      this.form.patchValue({ time });
      return;
    }
    const slot = this.availableTimes.find((s) => s.time === time);
    const isGroup = this.selectedPick?.itemType === ITEM_TYPE_GROUP;
    const rawIds = slot?.availableResourceIds;
    if (isGroup) {
      const idsLen = rawIds?.length ?? 0;
      this.loadingGroupResources = idsLen > 1;
      this.groupResourceListEpoch++;
    } else {
      this.loadingGroupResources = false;
      this.syncReservationPriceForCurrentSelection();
    }

    const [hours, minutes] = time.split(':').map(Number);
    const startTimeDate = new Date(day);
    startTimeDate.setHours(hours, minutes, 0);
    const endTimeDate = new Date(startTimeDate);
    endTimeDate.setMinutes(startTimeDate.getMinutes() + (Number.isFinite(this.periodHours) ? this.periodHours : 0));
    this.reservation.startTime = DateTime.fromJSDate(startTimeDate).toISO()!;
    this.reservation.endTime = DateTime.fromJSDate(endTimeDate).toISO()!;
    if (rawIds != null) {
      this.reservation.resourceIdsForSelectedDuration = [...rawIds];
    } else {
      this.reservation.resourceIdsForSelectedDuration = undefined;
    }
    this.activeTime = time;
    this.form.patchValue({ time });

    if (isGroup) {
      const ids = this.reservation.resourceIdsForSelectedDuration;
      if (ids?.length === 1) {
        this.applyGroupResourceSelection(ids[0]);
      } else if (ids && ids.length > 1) {
        this.selectedGroupResourceId = null;
        this.clearReservationPrice();
        // Un fotograma de skeleton para que el @for amb nou `track` es pinti sense flash.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.loadingGroupResources = false;
          });
        });
      } else {
        this.selectedGroupResourceId = null;
        this.loadingGroupResources = false;
        this.clearReservationPrice();
      }
    } else {
      this.selectedGroupResourceId = null;
    }
  }

  validateForm(): void {
    this.submitAttempt = true;
    this.form.markAllAsTouched();
    if (this.needsGroupResourcePicked()) {
      return;
    }
    const hasDuration = !this.resourceSlots.length || this.selectedSlot != null;
    if (
      !hasDuration ||
      !this.form.valid ||
      !this.activeCenter ||
      !this.activeService ||
      this.groupHasNoResources
    ) {
      return;
    }
    this.reservation.centerId = this.activeCenter.id;
    this.reservation.centerName = this.activeCenter.name;
    this.reservation.serviceId = this.activeService.id;
    this.reservation.serviceName = this.activeService.name;
    const centerFields: CustomFieldDTO[] = (this.activeCenter as any)?.customFields || [];
    const serviceFields: CustomFieldDTO[] = (this.activeService as any)?.customFields || [];
    const allFields = [...centerFields, ...serviceFields];
    const uniqueById = new Map<number, CustomFieldDTO>();
    allFields.forEach((cf) => {
      if (cf && typeof cf.id === 'number' && !uniqueById.has(cf.id)) {
        uniqueById.set(cf.id, cf);
      }
    });
    this.reservation.customFields = Array.from(uniqueById.values());
    this.formValid.emit(true);
  }

  private refreshCenterContextLabels(): void {
    this.refreshCenterContextLabelsFromDetail(this.activeCenter);
  }

  private refreshCenterContextLabelsFromDetail(detail: CenterDTO | null | undefined): void {
    if (!detail) {
      this.contextCenterLocation = null;
      return;
    }
    const c = detail as CenterDTO & Record<string, unknown>;
    const anyC = detail as unknown as Record<string, unknown>;
    const pick = (...vals: unknown[]): string => {
      for (const v of vals) {
        if (typeof v === 'string') {
          const s = v.trim();
          if (s) {
            return s;
          }
        }
      }
      return '';
    };
    const line = pick(
      c.location,
      c.address,
      c.locationName,
      c.formattedAddress,
      anyC['Address'] as string | undefined,
      anyC['LocationName'] as string | undefined,
      anyC['Location'] as string | undefined,
      anyC['location'] as string | undefined,
    );
    this.contextCenterLocation = line || null;
  }

  /** Mateixa semàntica que l'app (`lastPlaces` a la graella). */
  hourSlotLastPlaces(t: TimeAvailabilityDTO): boolean {
    return !this.timeSlotDisabled(t) && this.timeSlotFewLeft(t);
  }

  hourSlotLabelKey(t: TimeAvailabilityDTO): string {
    if (this.timeSlotDisabled(t)) {
      return 'PRIORAPPOINTMENT.notAvailable';
    }
    if (this.timeSlotFewLeft(t)) {
      return 'PRIORAPPOINTMENT.lastPlaces';
    }
    return 'PRIORAPPOINTMENT.available';
  }

  timeSlotDisabled(t: TimeAvailabilityDTO): boolean {
    return this.timeSlotAvailableCount(t) === 0;
  }

  /**
   * Últimes places (taronja): mateixa lògica que `updateVisibleTimesForGrid` de l'app
   * (`date-hour-selector`): ratio &lt; 20% o exactament 1 plaça amb total &gt; 1.
   */
  timeSlotFewLeft(t: TimeAvailabilityDTO): boolean {
    if (this.timeSlotDisabled(t)) {
      return false;
    }
    const rawAvail = t.availableSlots;
    if (rawAvail == null) {
      return false;
    }
    const avail = Number(rawAvail);
    const total = Number(t.totalSlots);
    if (!Number.isFinite(avail) || avail <= 0) {
      return false;
    }
    return (
      (total > 0 && avail < 0.2 * total) ||
      (avail === 1 && total > 1)
    );
  }

  trackByCardId(_i: number, item: CardItem): number {
    return item.itemType === ITEM_TYPE_GROUP ? -item.id : item.id;
  }

  /** Evita expresiones `track` con `??` en plantilla (bug compilador `tmp_xx is not defined`). */
  trackResourceSlot(index: number, slot: ResourceSlotDTO): string {
    const rid = slot.resourceId ?? 0;
    const sid = slot.id ?? 0;
    return `${slot.durationMinutes}-${rid}-${sid}-${index}`;
  }

  trackTimeAvailability(index: number, t: TimeAvailabilityDTO): string {
    return `${t.time}-${index}`;
  }

  trackGroupResource(index: number, svc: ServiceDTO): string {
    return `${this.groupResourceListEpoch}-${svc.id}-${index}`;
  }
}
